import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { localizedPath, useLang } from "@/lib/i18n";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import NextTests from "@/components/NextTests";
import { FollowMindovia } from "@/components/SocialLinks";
import { Share2, Sparkles, ArrowRight } from "lucide-react";
import useSEO from "@/lib/useSEO";

/**
 * Public Share page — PII-free result summary displayed via a random token.
 * Shows: instrument name, the result card image,
 *        and a CTA to try the instrument.
 */
export default function Share() {
  const { token } = useParams();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardUrl, setCardUrl] = useState(null);

  useSEO({
    title: lang === "id" ? "Ringkasan Refleksi — Mindovia" : "Reflection Summary — Mindovia",
    description: lang === "id" ? "Ringkasan refleksi pribadi yang dibagikan melalui Mindovia." : "A private reflection summary shared through Mindovia.",
    url: null,
    robots: "noindex, nofollow, noarchive",
  });

  useEffect(() => {
    let cancelled = false;
    api.get(`/public/share/${token}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [token]);

  // Fetch OG page indirectly to trigger card gen (best-effort) — or just request the image URL directly:
  useEffect(() => {
    if (!data) return;
    // The OG endpoint auto-generates the card. We call it lightly (HEAD) so admin doesn't have to.
    const code = data.result?.primary_dimension?.code; // not used; we need anonymous_code but we don't expose it
    // Instead: derive predictable path — server maps token → code internally; we can only trust /api/og/share/{token} to trigger generation, then compute image after fetch summary.
    // Simplest UX: hit OG page once (fire-and-forget) then set image URL to a known convention.
    fetch(`/api/og/share/${token}`, { method: "GET" }).catch(() => {});
    // We don't know anonymous_code; we set image from OG parse instead.
    fetch(`/api/og/share/${token}`).then(r => r.text()).then((html) => {
      const m = html.match(/property="og:image" content="([^"]+)"/);
      if (m && m[1]) setCardUrl(m[1]);
    }).catch(() => {});
  }, [data, token]);

  if (loading) return <div className="py-20 text-center text-muted-foreground">…</div>;
  if (!data) return <div className="py-20 text-center">Tautan tidak ditemukan.</div>;

  const inst = data.instrument;
  const primary = data.result.primary_dimension;
  const instName = (lang === "en" ? inst.name_en : inst.name_id) || inst.name_id;
  const primaryName = primary ? (lang === "en" ? primary.name_en : primary.name_id) : "";

  const nativeShare = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: instName, text: `${instName} — ${primaryName}`, url }); } catch {} }
    else { navigator.clipboard.writeText(url); }
  };

  return (
    <div className="min-h-screen bg-background" data-wrap="responsive">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-terracotta-700 mb-4">
          <Sparkles className="w-4 h-4" />
          {lang === "id" ? "Ringkasan Refleksi" : "Reflection Summary"}
        </div>
        <h1 className="font-display text-3xl md:text-5xl tracking-tight leading-tight break-words" data-testid="share-title">
          {instName}
        </h1>
        {inst.short_description_id && (
          <p className="text-muted-foreground mt-3 max-w-2xl break-words">{lang === "en" ? inst.short_description_en : inst.short_description_id}</p>
        )}

        {cardUrl && (
          <div className="mt-8 flex justify-center">
            <img src={cardUrl} alt={instName} className="max-w-full w-full md:w-96 rounded-2xl shadow-lg border border-border" data-testid="share-card-image" />
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          <Button onClick={nativeShare} variant="outline" className="rounded-full" data-testid="share-btn-native">
            <Share2 className="w-4 h-4 mr-2" /> {lang === "id" ? "Bagikan" : "Share"}
          </Button>
          <Link to={localizedPath(`/instruments/${inst.slug}`, lang)} className="ml-auto">
            <Button className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50" data-testid="share-cta-try">
              {lang === "id" ? "Coba tes ini" : "Take this test"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        <NextTests currentSlug={inst.slug} />
        <FollowMindovia />

        <div className="mt-10 text-xs text-muted-foreground border-t pt-6">
          {lang === "id" ? "Hasil eksplorasi diri, bukan diagnosis profesional." : "Self-exploration result, not a professional diagnosis."}
        </div>
      </div>
    </div>
  );
}
